import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, Calendar, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { getNewsPosts, getNewsPost } from '../services/api';
import { newsItems as fallbackNews } from '../data/mock';

const NewsPage = () => {
  const { slug } = useParams();
  const [posts, setPosts] = useState([]);
  const [currentPost, setCurrentPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        if (slug) {
          // Fetch single post
          const post = await getNewsPost(slug);
          setCurrentPost(post);
        } else {
          // Fetch all posts
          const data = await getNewsPosts();
          setPosts(data && data.length > 0 ? data : fallbackNews);
        }
      } catch (err) {
        console.error('Error fetching news:', err);
        // Use fallback data
        if (!slug) {
          setPosts(fallbackNews);
        } else {
          // Try to find in fallback
          const fallbackPost = fallbackNews.find(p => p.slug === slug);
          if (fallbackPost) {
            setCurrentPost(fallbackPost);
          } else {
            setError('Post not found');
          }
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Single post view
  if (slug) {
    if (loading) {
      return (
        <main>
          <div className="page-header">
            <div className="container">
              <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
                <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            </div>
          </div>
          <style>{`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </main>
      );
    }

    if (error || !currentPost) {
      return (
        <main>
          <div className="page-header">
            <div className="container">
              <h1 className="display-medium">Post Not Found</h1>
              <p className="body-large">The article you&apos;re looking for doesn&apos;t exist.</p>
              <Link to="/news" className="btn-primary" style={{ marginTop: '24px', display: 'inline-flex' }}>
                <ArrowLeft size={18} style={{ marginRight: '8px' }} />
                Back to News
              </Link>
            </div>
          </div>
        </main>
      );
    }

    return (
      <main>
        <div className="page-header">
          <div className="container">
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <ChevronRight size={16} />
              <Link to="/news">News</Link>
              <ChevronRight size={16} />
              <span>{currentPost.title}</span>
            </div>
            <span className="body-small" style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '16px' }}>
              <Calendar size={14} />
              {formatDate(currentPost.created_at || currentPost.date)}
            </span>
            <h1 className="display-medium">{currentPost.title}</h1>
          </div>
        </div>

        <section className="section">
          <div className="container" style={{ maxWidth: '800px' }}>
            {currentPost.image && (
              <img 
                src={currentPost.image}
                alt={currentPost.title}
                style={{ 
                  width: '100%', 
                  height: '400px', 
                  objectFit: 'cover', 
                  borderRadius: '24px',
                  marginBottom: '40px'
                }}
              />
            )}
            
            <div className="body-large" style={{ lineHeight: 1.8 }}>
              {currentPost.content ? (
                <div dangerouslySetInnerHTML={{ __html: currentPost.content.replace(/\n/g, '<br/>') }} />
              ) : (
                <p>{currentPost.excerpt}</p>
              )}
            </div>

            <div style={{ marginTop: '48px', paddingTop: '24px', borderTop: '1px solid var(--border-medium)' }}>
              <Link to="/news" className="btn-secondary">
                <ArrowLeft size={18} style={{ marginRight: '8px' }} />
                Back to All News
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  // News listing view
  return (
    <main>
      <div className="page-header">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <ChevronRight size={16} />
            <span>News & Updates</span>
          </div>
          <h1 className="display-medium">News & Updates</h1>
          <p className="body-large">
            The latest from The Grazing Trust – stories, updates, and insights.
          </p>
        </div>
      </div>

      <section className="section">
        <div className="container">
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
              <Loader2 size={32} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : posts.length > 0 ? (
            <>
              {/* Featured Post */}
              <Link 
                to={`/news/${posts[0].slug}`}
                className="network-card hover-lift"
                style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '40px', 
                  marginBottom: '48px',
                  textDecoration: 'none'
                }}
              >
                <img 
                  src={posts[0].image} 
                  alt={posts[0].title}
                  style={{ width: '100%', height: '300px', objectFit: 'cover', borderRadius: '16px' }}
                />
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <span 
                    className="body-small" 
                    style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px',
                      marginBottom: '12px'
                    }}
                  >
                    <Calendar size={14} />
                    {formatDate(posts[0].created_at || posts[0].date)}
                  </span>
                  <h2 className="heading-1" style={{ marginBottom: '16px' }}>{posts[0].title}</h2>
                  <p className="body-medium" style={{ marginBottom: '24px' }}>{posts[0].excerpt}</p>
                  <span 
                    style={{ 
                      color: 'var(--brand-dark)', 
                      fontWeight: 600, 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '6px' 
                    }}
                  >
                    Read more <ArrowRight size={16} />
                  </span>
                </div>
              </Link>

              {/* Other Posts */}
              {posts.length > 1 && (
                <div className="grid-3">
                  {posts.slice(1).map((post) => (
                    <Link 
                      key={post.id}
                      to={`/news/${post.slug}`}
                      className="network-card hover-lift"
                      style={{ textDecoration: 'none' }}
                    >
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="card-image"
                      />
                      <span 
                        className="body-small" 
                        style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '6px',
                          marginBottom: '8px'
                        }}
                      >
                        <Calendar size={14} />
                        {formatDate(post.created_at || post.date)}
                      </span>
                      <h3 className="heading-3" style={{ marginBottom: '8px' }}>{post.title}</h3>
                      <p className="body-medium">{post.excerpt}</p>
                    </Link>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '48px' }}>
              <p className="body-medium">No news articles yet. Check back soon for updates!</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <h2 className="display-small">Stay Updated</h2>
          <p className="body-large" style={{ maxWidth: '600px', margin: '0 auto 32px' }}>
            Follow our journey and the stories of the horses we help.
          </p>
          <Link to="/contact" className="btn-cta">Get in Touch</Link>
        </div>
      </section>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </main>
  );
};

export default NewsPage;
